'use client';

import { X, Sparkles, Check } from 'lucide-react';
import Link from 'next/link';

interface SubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookTitle?: string;
}

export default function SubscribeModal({ isOpen, onClose, bookTitle }: SubscribeModalProps) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        animation: 'fadeIn 0.3s ease-out',
      }}
      onClick={onClose}
    >
      <div
        style={{
          maxWidth: '600px',
          width: '100%',
          background: 'white',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          animation: 'scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '32px 24px',
            position: 'relative',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'rotate(0deg)';
            }}
          >
            <X size={20} color="white" />
          </button>

          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                marginBottom: '16px',
              }}
            >
              <Sparkles size={40} color="white" />
            </div>
            <h2
              style={{
                color: 'white',
                fontSize: '2rem',
                fontWeight: 900,
                margin: '0 0 12px 0',
              }}
            >
              Subscribe to Watch
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.125rem', margin: 0 }}>
              {bookTitle
                ? `Unlock "${bookTitle}" and thousands more!`
                : 'Unlock unlimited access to all stories'}
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '32px 24px' }}>
          {/* Features */}
          <div style={{ marginBottom: '32px' }}>
            {[
              'Unlimited audio storybooks',
              'Access to all languages',
              'Montessori educational content',
              'Download for offline listening',
              'Ad-free experience',
              'New stories added weekly',
            ].map((feature, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 0',
                  borderBottom: i < 5 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Check size={16} color="white" strokeWidth={3} />
                </div>
                <span style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: 500 }}>
                  {feature}
                </span>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f4ff 100%)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              border: '2px solid #667eea',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 600 }}>
                  Amaya Pro
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 900, color: '#667eea', lineHeight: 1 }}>
                  $20
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: '#64748b' }}>/month</span>
                </div>
              </div>
              <div
                style={{
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Best Value
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Link href="/pricing">
            <button
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.125rem',
                fontWeight: 800,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Sparkles size={20} />
              Start Subscription
            </button>
          </Link>

          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem', marginTop: '16px' }}>
            Cancel anytime. No hidden fees.
          </p>
        </div>
      </div>

      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

